import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { assign, get, includes, isEmpty, map, toNumber } from 'lodash';
import { Row, Col, Form, Button, InputGroup } from 'react-bootstrap';

import {
  CONNECTOR_SUBTYPES,
  CONNECTOR_TYPES,
  FILE_TYPES,
  getSubTypesForType
} from '../../../api/Connectors/Connectors';
import * as connectorOps from '../../../api/Connectors/connector-operations';

import methodPromise from '../../../modules/method-promise-wrapper';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import { filePathRegex } from '../../../modules/regex-utils';

import ThematicLayerTable from '../ThematicLayerTable';
import Validation from '../../containers/Validation';

const STATUS_OK = 'ok';
const DROPBOX_GENERIC = 'www.dropbox.com';
const REGEX_FOR_ANYTHING = /.*/i;

class ConnectorEditor extends Component {
  domainField = React.createRef();

  tableElement = React.createRef();

  state = {
    disabled: !connectorOps.isOutput(this.props.connector),
    hasDomain: connectorOps.hasDomain(this.props.connector),
    height: 500,
    passwordHidden: true,
    tableTitle: [],
    tableBody: [],
    type: connectorOps.getType(this.props.connector)
  };

  getConnectorFromForm(form) {
    const { type, hasDomain } = this.state;
    const connector = {
      isOutput: form.isOutput.checked,
      name: form.name.value.trim(),
      subtype: form.subtype.value.trim(),
      type
    };
    if (hasDomain) assign(connector, { domain: form.domain.value.trim() });
    else
      assign(connector, {
        ip: form.ip.value.trim(),
        port: toNumber(form.port.value)
      });
    if (includes([CONNECTOR_TYPES.POSTGRESQL, CONNECTOR_TYPES.FTP], type))
      assign(connector, {
        user: form.user.value.trim(),
        password: form.password.value
      });
    if (type === CONNECTOR_TYPES.POSTGRESQL)
      assign(connector, {
        databaseName: form.databaseName.value.trim(),
        schema: form.schema.value.trim()
      });
    if (type !== CONNECTOR_TYPES.POSTGRESQL)
      assign(connector, {
        filePath: form.filePath.value.trim(),
        fileType: form.fileType.value.trim()
      });
    return connector;
  }

  checkConnector = async event => {
    try {
      const connector = this.getConnectorFromForm(event.target.form);
      const { status, message } = await methodPromise('connectors.checkIfValid', connector);
      if (status === STATUS_OK) {
        this.setState({ disabled: false });
        Bert.alert(message, 'success');
      } else {
        throw new Error(message);
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  disableButton = () => this.setState({ disabled: true });

  handleSubmit = async form => {
    const { connector } = this.props;

    const existingConnector = connector && connectorOps.getId(connector);
    const methodToCall = existingConnector ? 'connectors.update' : 'connectors.insert';

    // TODO: Cuando se cambia el tipo y se hace update los antiguos campos no se borran, por lo que en el view siguen apareciendo. Esta no es una buena solución pero la dejo así hasta que se me ocurra una solución mejor.
    const newConnector = this.getConnectorFromForm(form);

    if (existingConnector) newConnector._id = existingConnector;

    try {
      const connectorId = await methodPromise(methodToCall, newConnector);
      this.redirectToConnectorView(form, existingConnector, connectorId);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleTypeChange = event => this.setState({ disabled: true, type: event.target.value.trim() });

  listSources = async event => {
    try {
      const connector = this.getConnectorFromForm(event.target.form);
      const { tableBody, tableTitle } = await methodPromise('connectors.listSources', connector);
      this.setState({ tableBody, tableTitle });
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleDomain = () =>
    this.setState(prevState => ({ disabled: true, hasDomain: !prevState.hasDomain }));

  togglePassword = () =>
    this.setState(prevState => ({ passwordHidden: !prevState.passwordHidden }));

  redirectToConnectorView(form, existingConnector, connectorId) {
    const { history } = this.props;
    const confirmation = existingConnector ? 'Connection updated!' : 'Connection added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/connections/${connectorId}`);
  }

  render() {
    const { connector } = this.props;
    const { disabled, hasDomain, height, passwordHidden, tableBody, tableTitle, type } = this.state;
    const isNewConnector = isEmpty(connectorOps.getId(connector));

    const validationProps = {
      rules: {
        name: {
          required: true
        },
        type: {
          required: true
        },
        subtype: {
          required: true
        },
        ip: {
          required: !hasDomain
        },
        port: {
          required: !hasDomain
        },
        domain: {
          required: hasDomain
        },
        user: {
          required: includes([CONNECTOR_TYPES.POSTGRESQL, CONNECTOR_TYPES.FTP], type)
        },
        password: {
          required: includes([CONNECTOR_TYPES.POSTGRESQL, CONNECTOR_TYPES.FTP], type)
        },
        databaseName: {
          required: type === CONNECTOR_TYPES.POSTGRESQL
        },
        schema: {
          required: type === CONNECTOR_TYPES.POSTGRESQL
        },
        fileType: {
          required: type !== CONNECTOR_TYPES.POSTGRESQL
        },
        filePath: {
          required: type !== CONNECTOR_TYPES.POSTGRESQL,
          format: includes(get(this, 'domainField.current.value', ''), DROPBOX_GENERIC)
            ? REGEX_FOR_ANYTHING
            : filePathRegex
        },
        isOutput: {
          required: false
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss'
        },
        type: {
          required: 'This needs a type please'
        },
        subtype: {
          required: 'This needs a subtype please'
        },
        ip: {
          required: 'This needs an IP please'
        },
        port: {
          required: 'This needs a port number please'
        },
        domain: {
          required: 'This needs a domain please'
        },
        user: {
          required: 'This needs a user please'
        },
        password: {
          required: 'This needs a password please'
        },
        databaseName: {
          required: 'This needs a name for the database please'
        },
        schema: {
          required: 'This needs a name for the schema please'
        },
        filePath: {
          required: 'This needs a file path please',
          format: 'File extension and/or trailing slash not allowed'
        },
        fileType: {
          required: 'This needs a file type please'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };

    return (
      <>
        <Row>
          <Col xs={12} md={6} lg={4}>
            <Validation {...validationProps}>
              <Form onSubmit={event => event.preventDefault()}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    defaultValue={connectorOps.getName(connector)}
                    type="text"
                    name="name"
                    placeholder="Oh, The Places You'll Go!"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Control
                    as="select"
                    value={type}
                    name="type"
                    onChange={this.handleTypeChange}
                  >
                    {map(CONNECTOR_TYPES.properties, ({ value, humanValue }) => {
                      if (humanValue !== 'WMS' && humanValue !== 'GeoJSON VT') {
                        return (
                          <option value={value} key={value}>
                            {humanValue}
                          </option>
                        );
                      }
                      return false;
                    })}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Subtype</Form.Label>
                  <Form.Control
                    as="select"
                    defaultValue={connectorOps.getSubType(connector)}
                    name="subtype"
                  >
                    <option value="">-</option>
                    {map(getSubTypesForType(type, 'object'), (value, key) => {
                      return (
                        <option value={value} key={value}>
                          {CONNECTOR_SUBTYPES.properties[key].humanValue}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Form.Group>
                <Form.Group as={Row}>
                  <Col xs={12} md={6}>
                    <Form.Check
                      inline
                      type="radio"
                      id="useIPAndPort"
                      label="Use IP and Port"
                      checked={!hasDomain}
                      onChange={this.toggleDomain}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Check
                      inline
                      type="radio"
                      id="useDomain"
                      label="Use Domain"
                      checked={hasDomain}
                      onChange={this.toggleDomain}
                    />
                  </Col>
                </Form.Group>
                {hasDomain && (
                  <Form.Group>
                    <Form.Label>Domain</Form.Label>
                    <Form.Control
                      ref={this.domainField}
                      defaultValue={connectorOps.getDomain(connector)}
                      type="text"
                      name="domain"
                      placeholder="The URL of the domain"
                      onChange={this.disableButton}
                    />
                  </Form.Group>
                )}
                {!hasDomain && (
                  <>
                    <Form.Group>
                      <Form.Label>IP</Form.Label>
                      <Form.Control
                        defaultValue={connectorOps.getIp(connector)}
                        type="text"
                        name="ip"
                        placeholder="The IP of the server"
                        onChange={this.disableButton}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Port</Form.Label>
                      <Form.Control
                        defaultValue={connectorOps.getPort(connector)}
                        type="number"
                        name="port"
                        placeholder="3000"
                        min="0"
                        max="65535"
                        onChange={this.disableButton}
                      />
                    </Form.Group>
                  </>
                )}
                {includes([CONNECTOR_TYPES.POSTGRESQL, CONNECTOR_TYPES.FTP], type) && (
                  <>
                    <Form.Group>
                      <Form.Label>User</Form.Label>
                      <Form.Control
                        defaultValue={connectorOps.getUser(connector)}
                        type="text"
                        name="user"
                        placeholder="Your username in the server"
                        onChange={this.disableButton}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Password</Form.Label>
                      <InputGroup className="mb-3">
                        <Form.Control
                          defaultValue={connectorOps.getPassword(connector)}
                          type={passwordHidden ? 'password' : 'text'}
                          name="password"
                          placeholder="Your password in the server"
                          onChange={this.disableButton}
                        />
                        <InputGroup.Append>
                          <Button onClick={this.togglePassword} variant="outline-secondary">
                            <FontAwesomeIcon icon={passwordHidden ? 'eye-slash' : 'eye'} />
                          </Button>
                        </InputGroup.Append>
                      </InputGroup>
                    </Form.Group>
                  </>
                )}
                {type === CONNECTOR_TYPES.POSTGRESQL && (
                  <>
                    <Form.Group>
                      <Form.Label>Database Name</Form.Label>
                      <Form.Control
                        defaultValue={connectorOps.getDatabaseName(connector)}
                        type="text"
                        name="databaseName"
                        placeholder="Name of the PostgreSQL database"
                        onChange={this.disableButton}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Schema</Form.Label>
                      <Form.Control
                        defaultValue={connectorOps.getSchema(connector)}
                        type="text"
                        name="schema"
                        placeholder="Name of the schema of the database"
                        onChange={this.disableButton}
                      />
                    </Form.Group>
                  </>
                )}
                {type !== CONNECTOR_TYPES.POSTGRESQL && (
                  <>
                    <Form.Group>
                      <Form.Label>File Path</Form.Label>
                      <Form.Control
                        name="filePath"
                        defaultValue={connectorOps.getFilePath(connector)}
                        placeholder="Oh, The Places You'll Go!"
                        onChange={this.disableButton}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>File Type</Form.Label>
                      <Form.Control
                        as="select"
                        name="fileType"
                        defaultValue={connectorOps.getFileType(connector)}
                        onChange={this.disableButton}
                      >
                        <option value="">Select File Type...</option>
                        {map(FILE_TYPES.properties, ({ value, humanValue }) => (
                          <option value={value} key={value}>
                            {humanValue}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </>
                )}
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="isOutput"
                    label="Is output connector?"
                    defaultChecked={connectorOps.isOutput(connector)}
                    onChange={this.disableButton}
                  />
                </Form.Group>
                <Form.Row className="d-flex">
                  <Button disabled={disabled} type="submit" variant="success">
                    {isNewConnector ? 'Add Connection' : 'Save Changes'}
                  </Button>
                  <Button disabled={disabled} variant="secondary" onClick={this.listSources}>
                    List Sources
                  </Button>
                  <Button onClick={this.checkConnector}>Check Connection</Button>
                </Form.Row>
              </Form>
            </Validation>
          </Col>
          <Col xs={12} md={6} lg={8}>
            {!isEmpty(tableBody) && (
              <div ref={this.tableElement} className="h-100">
                <ThematicLayerTable
                  loading={false}
                  tableTitle={tableTitle}
                  tableBody={tableBody}
                  layerSubtype=""
                  isShared={false}
                  height={height}
                  changeSortIndex={() => ''}
                  sortIndex={{}}
                />
              </div>
            )}
          </Col>
        </Row>
      </>
    );
  }
}

ConnectorEditor.defaultProps = {
  connector: {
    name: '',
    type: CONNECTOR_TYPES.FTP,
    ip: '',
    port: '',
    user: '',
    password: ''
  }
};

ConnectorEditor.propTypes = {
  connector: PropTypes.object,
  history: PropTypes.object.isRequired
};

export default ConnectorEditor;
